import { useReducer, useTransition, useCallback } from 'react';

/**
 * Default comparator that compares objects by their .id property.
 * Works for any object with an id field (TDR.Table, etc.)
 *
 * Handles null/undefined explicitly:
 * - null === null → true (no update needed)
 * - undefined === undefined → true (no update needed)
 * - null !== object → false (update needed)
 * - object !== null → false (update needed)
 */
export const ID_COMPARATOR = <T extends { id?: string | number }>(
	prev: T | null | undefined,
	next: T | null | undefined
): boolean => {
	// Both nullish (null or undefined) - check strict equality
	if ((prev === null || prev === undefined) && (next === null || next === undefined)) {
		return prev === next; // null === null (true), undefined === undefined (true), null === undefined (false)
	}

	// One nullish, one not - not equal (React should update)
	if (prev === null || prev === undefined || next === null || next === undefined) {
		return false;
	}

	// Both non-nullish - compare by ID
	return prev.id === next.id;
};

/**
 * A custom hook for high-frequency object updates that prevents unnecessary re-renders.
 *
 * Uses React's native bailout mechanism (useReducer) + startTransition for non-urgent updates.
 * Perfect for 60 FPS camera updates, mouse tracking, or scroll listeners.
 *
 * @param initialValue - The starting state
 * @param comparator - Custom comparison logic. Defaults to ID_COMPARATOR (compares by .id field).
 * @returns [state, setState, isPending] - Similar to useState but with automatic bailout
 *
 * @example
 * // Simple ID-based equality (default)
 * const [table, setTable, isPending] = useSmoothState<TDR.Table | null>(null);
 * setTable(newTable); // Only re-renders if newTable.id !== table.id
 *
 * @example
 * // Custom comparator
 * const [data, setData] = useSmoothState(
 *   initialData,
 *   (prev, next) => prev?.timestamp === next?.timestamp
 * );
 */
export function useSmoothState<T>(
	initialValue: T,
	comparator: (prev: T, next: T) => boolean = ID_COMPARATOR
) {
	const [isPending, startTransition] = useTransition();

	// The Reducer: Centralizes the "should I update?" logic.
	// Returning the existing 'state' reference triggers a React bailout (no re-render).
	const stateReducer = (state: T, nextValue: T): T => {
		const isEqual = comparator(state, nextValue);
		return isEqual ? state : nextValue;
	};

	const [state, dispatch] = useReducer(stateReducer, initialValue);

	// The Setter: Wrapped in useCallback to keep the function reference stable.
	const setState = useCallback((nextValue: T) => {
		// startTransition: Marks the update as non-urgent.
		// This allows the browser to prioritize high-priority tasks (like scrolling/typing).
		startTransition(() => {
			dispatch(nextValue);
		});
	}, []);

	return [state, setState, isPending] as const;
}
